import { Section } from '@/components/shared';
import { COGNITO_ERRORS, ROUTE_PATHS } from '@/constants';
import { useAppDispatch, useAppSelector, useAuth } from '@/hooks';
import { updateUser } from '@/store/userSlice';
import type { IAuthFormFields, ICognitoError, ICompany, IUser } from '@/types';
import { pathJoin, scrollToTop } from '@/utils';
import { Auth } from '@aws-amplify/auth';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Flex,
  Heading,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useControllableState,
} from '@chakra-ui/react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CompanyForm, LoginForm, UserForm } from '../components';

export const UserInfoStep: React.FC = (): JSX.Element => {
  const navigate = useNavigate();
  const { authUser } = useAuth();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.users.user);
  const [loginError, setLoginError] = useControllableState({ defaultValue: '' });

  scrollToTop();

  const isLogged = authUser?.isValid();
  const isCompany = 'vat' in (user ?? {});

  const goNextStep = (replace = false): void => {
    const path = pathJoin([`/${ROUTE_PATHS.quote}`, ROUTE_PATHS.quoteAuctionHouseInfo]);
    navigate(path, { replace });
  };

  const handleUserFormSubmit = (data: IUser | ICompany): void => {
    dispatch(updateUser(data));
    goNextStep();
  };

  const handleLoginFormSubmit = async ({ email, password }: IAuthFormFields): Promise<void> => {
    try {
      setLoginError('');
      await Auth.signIn(email.toLowerCase(), password);
      goNextStep(true);
    } catch (error) {
      const msg = COGNITO_ERRORS[(error as ICognitoError).code];
      setLoginError(msg);
    }
  };

  return (
    <Section
      containerProps={{ py: 0 }}
      headingProps={{ mt: 0 }}
      id="step-one"
      title={t('WIZARD.USER.STEP_TITLE')}
    >
      <Flex
        alignItems={{ base: 'center', md: 'flex-start' }}
        direction={{ base: 'column', md: 'row' }}
        gap={12}
        justifyContent="center"
        w="full"
      >
        <Box minW="55%" textAlign={{ base: 'center', md: 'left' }}>
          <Heading fontSize={{ base: 'h2.base', md: 'h2.md' }} fontWeight="normal" pb={2}>
            {t('WIZARD.USER.TITLE')}
          </Heading>
          <Text fontSize="large">
            <Trans i18nKey="WIZARD.USER.CONTENT" />
          </Text>
        </Box>
        <Flex flexBasis={{ base: 'full', lg: 'max-content' }}>
          <Tabs defaultIndex={isCompany && user ? 1 : 0} flexGrow={1} isLazy variant="mbe">
            <TabList gap={{ base: 4, md: 8 }}>
              <Tab>{t('WIZARD.USER.PERSON')}</Tab>
              <Tab>{t('WIZARD.USER.COMPANY')}</Tab>
              {!isLogged && <Tab>{t('WIZARD.USER.HAVE_U_ACCOUNT')}</Tab>}
            </TabList>
            <TabPanels>
              <TabPanel px={0} py={12}>
                <UserForm
                  defaultValues={!isCompany && user ? (user as IUser) : undefined}
                  onSubmit={handleUserFormSubmit}
                />
              </TabPanel>
              <TabPanel px={0} py={12}>
                <CompanyForm
                  defaultValues={isCompany && user ? (user as ICompany) : undefined}
                  onSubmit={handleUserFormSubmit}
                />
              </TabPanel>
              {!isLogged && (
                <TabPanel px={0} py={12}>
                  {loginError && (
                    <Alert mb={8} status="error">
                      <AlertIcon />
                      <AlertDescription>{t(loginError)}</AlertDescription>
                    </Alert>
                  )}
                  {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
                  <LoginForm onSubmit={handleLoginFormSubmit} />
                </TabPanel>
              )}
            </TabPanels>
          </Tabs>
        </Flex>
      </Flex>
    </Section>
  );
};
